import { IUserAppSettings } from "@vmosolutions/oc-interfaces";
import { Dispatch } from "redux";
import {
  IEpochRange,
  IFlightPairingStatusMeta,
  IFlightPairingStatusPage,
  IFlightPairingStatusRecomputeResponse,
  IFlightPairingStatusRow,
} from "src/pages/FlightPairingStatusV2/pairingStatusTypes";
import { IFlightPairingStatusUserSettings } from "src/pages/FlightPairingStatusV2/pairingStatusUserSettings";
import AmplifyAPIWrapper, { INotifications } from "./AmplifyAPIWrapper";

const PAGE_SIZE = 2000;
/** Guard against a runaway pagination loop if the server ever returns a non-advancing cursor. */
const MAX_PAGES = 50;

export interface IFlightPairingStatusLoadResult {
  rows: IFlightPairingStatusRow[];
  meta: IFlightPairingStatusMeta | null;
}

/**
 * Load every cached flight in the range (all pages). Sub/planning aircraft are always included; hiding them is
 * a client-side filter so toggling it never needs a request.
 * @returns null when any page failed (the wrapper has already surfaced the error).
 */
export const fetchFlightPairingStatus = async (
  range: IEpochRange,
  dispatch?: Dispatch,
  cancelHook?: (p: Promise<any>) => Promise<any>
): Promise<IFlightPairingStatusLoadResult | null> => {
  const rows: IFlightPairingStatusRow[] = [];
  let meta: IFlightPairingStatusMeta | null = null;
  let lastId: string | null = null;
  const notifications: INotifications | undefined = dispatch
    ? { dispatch, errorMessage: "Unable to load flight pairing status" }
    : undefined;

  for (let page = 0; page < MAX_PAGES; page++) {
    const query = `incsub=true&limit=${PAGE_SIZE}${lastId ? `&lastId=${encodeURIComponent(lastId)}` : ""}`;
    const response = await AmplifyAPIWrapper.Get<IFlightPairingStatusPage>(
      `/flights/pairingStatusV2/${range.start}/${range.end}?${query}`,
      notifications,
      cancelHook
    );
    if (!response || !Array.isArray(response.queryResult)) return null;
    rows.push(...response.queryResult);
    if (response.meta) meta = response.meta;
    if (!response.lastId || response.lastId === lastId) break;
    lastId = response.lastId;
  }
  return { rows, meta };
};

/**
 * Ask the backend to re-evaluate a range (or specific flights) right now. Other clients are told through the
 * websocket; the caller reloads its own range afterwards.
 */
export const requestFlightPairingStatusRecompute = async (
  target: IEpochRange | { flightIds: string[] },
  dispatch?: Dispatch
): Promise<IFlightPairingStatusRecomputeResponse | null> => {
  const notifications: INotifications | undefined = dispatch
    ? { dispatch, errorMessage: "Unable to recompute flight pairing status" }
    : undefined;
  return AmplifyAPIWrapper.Post<IFlightPairingStatusRecomputeResponse>(
    "/flights/pairingStatusV2/recompute",
    target,
    notifications
  );
};

/**
 * Save the window's per-user state (last filters, view) to the user's profile. Returns the stored settings, or
 * null when the request failed (the caller keeps working with its in-memory state either way).
 */
export const saveFlightPairingStatusUserSettings = async (
  settings: IFlightPairingStatusUserSettings
): Promise<IFlightPairingStatusUserSettings | null> => {
  const response = await AmplifyAPIWrapper.Put<IUserAppSettings>("/me/settings", {
    flightPairingStatus: settings,
  });
  return response?.flightPairingStatus ?? null;
};
